import { format, parseISO, formatDistanceToNow } from 'date-fns';

/** The one date format used everywhere (DD-MMM-YYYY, e.g. 16-Oct-2026). Owner decision, ticket #405. */
export const DATE_FORMAT = 'dd-MMM-yyyy';
export const DATE_TIME_FORMAT = 'dd-MMM-yyyy HH:mm';

function toDate(date: string | Date | number): Date {
  return typeof date === 'string' ? parseISO(date) : new Date(date);
}

export function formatDate(
  date: string | Date | number | null | undefined,
  formatStr: string = DATE_FORMAT
): string {
  if (date === null || date === undefined || date === '') return '';
  try {
    return format(toDate(date), formatStr);
  } catch {
    return '';
  }
}

export function formatDateTime(date: string | Date | number | null | undefined): string {
  return formatDate(date, DATE_TIME_FORMAT);
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-IN');

/** Count / plain number with Indian digit grouping (12,34,567). Never locale-dependent. */
export function formatNumber(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? NUMBER_FORMAT.format(num) : '';
}

/** Rupees with Indian grouping and the rupee symbol (single shared currency formatter, ticket #405). */
export function formatCurrency(amount: string | number, currency: string = 'INR'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(num)) return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Rupees for a narrow stat card: 1 crore and above reads as "₹2.67 Cr" (non-breaking space, so it never
 * wraps mid-number); anything smaller keeps the full Indian-grouped amount. Pair it with the full amount in a
 * title so nothing is lost.
 */
export function formatCurrencyCompact(amount: string | number, currency: string = 'INR'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(num)) return '';
  if (Math.abs(num) < 1e7) return formatCurrency(num, currency);
  const cr = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 })
    .format(num / 1e7);
  return `${cr}\u00a0Cr`;
}

export function formatPhoneNumber(phone: string): string {
  // Basic formatting for Indian phone numbers
  if (phone.startsWith('+91')) {
    const cleaned = phone.slice(3);
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
  }
  return phone;
}

export function truncate(str: string, length: number = 50): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function titleCase(str: string): string {
  if (!str) return '';
  return str
    .split(/[_\s-]/)
    .map(word => capitalize(word))
    .join(' ');
}

/** KYC status for display: title case, except the hyphenated "Re-verification Required". */
export function kycStatusLabel(status: string): string {
  if (status === 'reverify_required') return 'Re-verification Required';
  return titleCase(status);
}

/**
 * Canonical vehicle registration number: uppercase, alphanumerics only
 * (separators/spaces stripped). Mirrors the backend lookup normalization
 * (policies/views.py) so "mh 01-ab 1234" -> "MH01AB1234". BROKER-10.
 */
export function formatRegistrationNumber(raw: string): string {
  return (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Characters allowed in a person name: any Unicode letter or combining mark
 * (accented Latin, Devanagari, Tamil, ...), space, hyphen, apostrophe, dot. */
const NAME_DISALLOWED = /[^\p{L}\p{M}\s'\u2019.-]/gu;

/**
 * Sentence-case a person name for input formatting: strip characters that
 * never belong in a name (keep any Unicode letter/mark, spaces, hyphen,
 * apostrophe, dot), collapse leading spaces, then capitalize the first letter
 * and lowercase the rest -- per the BROKER-10 examples ("AMAN RANA" ->
 * "Aman rana"). Real-time safe: appends at the caret end. Forms that must
 * SHOW a message for bad characters use `formatNameKeepInvalid` instead.
 */
export function formatName(raw: string): string {
  return formatNameKeepInvalid((raw || '').replace(NAME_DISALLOWED, ''));
}

/**
 * Same casing/leading-space rules as `formatName` but never drops a typed
 * character, so a digit stays visible and `personNameProblem` can explain it.
 *
 * Casing: the first letter is capitalized and letters inside a word are lowercased (BROKER-10: "aman rana" ->
 * "Aman rana"), but a letter typed right after a space, hyphen, apostrophe or dot is kept as typed, so
 * "Anne-Marie", "D'Souza" and "Müller Ñandi" survive key-by-key typing (R2-F6, #640). Text that is all capitals
 * with a word of two or more letters ("AMAN RANA", caps lock or a paste) is still sentence-cased.
 */
export function formatNameKeepInvalid(raw: string): string {
  const cleaned = (raw || '').replace(/^\s+/, '');
  if (!cleaned) return '';
  const shouting = !/\p{Ll}/u.test(cleaned) && /\p{Lu}{2,}/u.test(cleaned);
  if (shouting) return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
  let out = '';
  let atWordStart = true;
  let first = true;
  for (const ch of cleaned) {
    if (first) out += ch.toUpperCase();
    else out += atWordStart ? ch : ch.toLowerCase();
    atWordStart = /[\s\-'\u2019.]/u.test(ch);
    first = false;
  }
  return out;
}

/** Returns a message when a person name has characters outside the allowed set
 * (digits are the common case); '' when fine. Mirrors the backend
 * `person_name_problem`. */
export function personNameProblem(value: string | undefined | null): string {
  const text = (value || '').trim();
  if (!text) return '';
  if (/\d/.test(text)) return 'Name cannot contain digits';
  if (/[^\p{L}\p{M}\s'\u2019.-]/u.test(text)) {
    return "Name can only contain letters, spaces, hyphens, apostrophes and dots";
  }
  return '';
}
